import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu } from 'lucide-react';
import { useAuth } from '../../features/auth/AuthContext';
import { TOPBAR, LOGOUT } from '../../constants/testIds';
import { NotificationBell } from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export function Topbar({ title, onMenu }: { title: string; onMenu?: () => void }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.full_name || 'U')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const primaryRole = user?.roles?.[0] || 'member';

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <header
      className="h-20 w-full pl-0 lg:pl-64 fixed top-0 right-0 bg-[#050914]/85 backdrop-blur-md border-b border-white/10 flex items-center justify-between px-4 sm:px-6 lg:px-8 z-30"
      data-testid={TOPBAR.container}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onMenu}
          data-testid="topbar-menu-button"
          className="lg:hidden text-white/70 hover:text-white p-2 -ml-2 rounded-lg hover:bg-white/5 transition-colors shrink-0"
          aria-label="Buka menu"
        >
          <Menu className="w-6 h-6" strokeWidth={1.5} />
        </button>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">{primaryRole}</p>
          <h1 className="text-base sm:text-lg font-medium text-white tracking-tight truncate">{title}</h1>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
      <NotificationBell />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={TOPBAR.userMenu}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 hover:bg-white/[0.06] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            <div className="w-8 h-8 rounded-lg bg-cyan-400 text-[#050914] flex items-center justify-center text-xs font-semibold">
              {initials}
            </div>
            <div className="hidden sm:block text-left leading-tight">
              <p className="text-sm text-white" data-testid={TOPBAR.userName}>
                {user?.full_name}
              </p>
              <p className="text-xs text-white/40">{user?.email}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="bg-[#090D1A] border-white/10 text-white/80 w-56"
        >
          <DropdownMenuLabel className="text-white/50 font-normal">
            {user?.email}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          <DropdownMenuItem
            data-testid={LOGOUT.button}
            onClick={handleLogout}
            className="text-red-300 focus:text-red-200 focus:bg-red-500/10 cursor-pointer"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Keluar / Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
    </header>
  );
}
